import React, { useEffect, useRef, useState } from "react";
import fusoLogo from "../../assets/fuso-logo.svg";
import feedPnc from "../../assets/feedStarPnc.svg";
import historyClosePnc from "../../assets/toggle-button.svg";
import toggleBtn from "../../assets/historyToggleMobile.svg";
import ReactDOM from "react-dom";
import logoutPnc from "../../assets/logoutPnc.svg";
import aboutSide from "../../assets/aboutSidebarPnc.svg";
import "./Sidebar.css";
import ProfileSection from "../ProfileSection/ProfileSection.js";
import PinnedChats from "../PinnedChats/PinnedChats.js";
import HistorySection from "../HistorySection/HistorySection.js";
import ActionButtons from "../ActionButtons/ActionButtons.js";
import MobileFeedbackModal from "../MobileFeedbackModal/MobileFeedbackModal.js";
import { CHAT_API_ENDPOINTS } from "../../api/CHAT_API_ENDPOINTS";
import { useTranslation } from 'react-i18next';

const Sidebar = ({
  isSidebarOpen,
  toggleSidebar,
  resetToWelcomePage,
  toggleFeedbackModal,
  chatHistory,
  pinnedChats,
  selectedHistoryId,
  handleHistoryItemClick,
  isLoadingHistory,
  historyError,
  toggleDropdown,
  activeDropdownId,
  handleDeleteButtonClick,
  handlePinChat,
  handleUnpinButtonClick,
  toggleAboutModal,
  handleLogout,
  userId,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileFeedbackModal, setShowMobileFeedbackModal] = useState(false);
  const [rating, setRating] = useState(4); // Default to 4 stars
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    rating: false,
    feedback: false,
  });
  const [longPressTimer, setLongPressTimer] = useState(null);

  // Transform chatHistory if it comes as an object with numeric keys
  const normalizedChatHistory = React.useMemo(() => {
    if (!chatHistory) return [];
    
    // If chatHistory is already an array, use it as is
    if (Array.isArray(chatHistory)) {
      return chatHistory.map(item => ({
        ...item,
        // Ensure we have timestamp as Date object
        timestamp: item.timestamp || (item.time_stamp ? new Date(item.time_stamp) : new Date()),
      }));
    }
    
    // If chatHistory is an object with numeric keys, convert to array
    if (typeof chatHistory === 'object') {
      return Object.values(chatHistory).map(item => ({
        ...item,
        // Convert time_stamp to timestamp as Date object
        timestamp: item.time_stamp ? new Date(item.time_stamp) : new Date(),
        // Keep original field names as well
        chat_id: item.chat_id,
        pinned: item.pinned,
        title: item.title,
      }));
    }
    
    return [];
  }, [chatHistory]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsSmallScreen(window.innerWidth < 992);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    function handleClickOutsideSidebar(event) {
      if (!isMobile) return;
      if (isSidebarOpen) {
        const sidebarElement = document.querySelector(".sidebarPnc");
        if (sidebarElement && !sidebarElement.contains(event.target)) {
          toggleSidebar();
        }
      }
    }

    // Add the event listener to the document
    document.addEventListener("mousedown", handleClickOutsideSidebar);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSidebar);
    };
  }, [isSidebarOpen, isMobile, toggleSidebar]);

  const handleLongPressStart = (itemId, e) => {
    if (window.innerWidth >= 640) return;
    if (longPressTimer) clearTimeout(longPressTimer);
    const timer = setTimeout(() => {
      toggleDropdown(itemId, e);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  useEffect(() => {
    return () => {
      if (longPressTimer) clearTimeout(longPressTimer);
    };
  }, [longPressTimer]);

  // Profile menu handlers
  const handleProfileClick = () => {
    setShowProfileMenu(true);
  };

  const handleCloseProfileMenu = () => {
    setShowProfileMenu(false);
  };

  const handleLogoutClick = () => {
    handleLogout();
    setShowProfileMenu(false);
  };

  const handleAboutChatbot = () => {
    toggleAboutModal();
    setShowProfileMenu(false);
  };

  const handleFeedbackButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMobile) {
      setShowMobileFeedbackModal(true);
    } else {
      toggleFeedbackModal();
    }
  };

  const handleCloseMobileFeedbackModal = () => {
    setShowMobileFeedbackModal(false);
    setRating(4);
    setFeedback("");
    setValidationErrors({
      rating: false,
      feedback: false,
    });
    setSubmitError(null);
  };

  // Validate feedback form
  const validateForm = () => {
    const errors = {
      rating: rating === 0,
      feedback: feedback.trim() === "",
    };

    setValidationErrors(errors);
    return !errors.rating && !errors.feedback;
  };

  // Submit feedback and close the modal
  const handleSubmitFeedback = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await fetch(CHAT_API_ENDPOINTS.ADD_FEEDBACK, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // user_id: userId,
          user_id:
            sessionStorage.getItem("user_id") ||
            localStorage.getItem("user_id") ||
            userId ||
            "",
          rating: rating,
          feedback: feedback,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      setSubmitSuccess(true);
      setShowSuccessToast(true);
      setTimeout(() => {
        setRating(4);
        setFeedback("");
        setSubmitSuccess(false);
        setShowMobileFeedbackModal(false);
        setTimeout(() => {
          setShowSuccessToast(false);
        }, 2000);
      }, 1500);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if the viewport is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle click outside the dropdown menu to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown(null, event);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [toggleDropdown]);

  // Helper function to check if a date is today
  const isToday = (date) => {
    if (!date || !(date instanceof Date)) return false;
    const today = new Date();
    return isSameUTCDay(date, today);
  };

  // Helper function to check if a date is yesterday
  const isYesterday = (date) => {
    if (!date || !(date instanceof Date)) return false;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return isSameUTCDay(date, yesterday);
  };

  // Helper function to check if dates are the same day (UTC)
  const isSameUTCDay = (date1, date2) => {
    if (!date1 || !date2 || !(date1 instanceof Date) || !(date2 instanceof Date)) return false;
    const d1 = getUTCDateParts(date1);
    const d2 = getUTCDateParts(date2);
    return d1.year === d2.year && d1.month === d2.month && d1.day === d2.day;
  };

  // Helper function to get UTC date parts
  const getUTCDateParts = (date) => {
    if (!date || !(date instanceof Date)) return { year: 0, month: 0, day: 0 };
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
    };
  };

  // Specific check for dates within last 7 days (excluding today and yesterday)
  const isWithinLast7Days = (date) => {
    if (!date || !(date instanceof Date)) return false;
    const now = new Date();
    // Check date against each day from 2-7 days ago
    for (let i = 2; i <= 7; i++) {
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - i);

      if (isSameUTCDay(date, pastDate)) {
        return true;
      }
    }
    return false;
  };

  // Specific check for dates within last 30 days (excluding last 7 days)
  const isWithinLast30Days = (date) => {
    if (!date || !(date instanceof Date)) return false;
    const now = new Date();
    // Check date against each day from 8-30 days ago
    for (let i = 8; i <= 30; i++) {
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - i);

      if (isSameUTCDay(date, pastDate)) {
        return true;
      }
    }
    return false;
  };

  // Check for dates older than 30 days
  const isOlderThan30Days = (date) => {
    if (!date || !(date instanceof Date)) return false;
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    return date < thirtyDaysAgo;
  };

  // Filter functions that maintain the original sort order
  const getTodayHistory = () => {
    const filteredItems = normalizedChatHistory.filter(
      (item) => isToday(item.timestamp) && item.pinned !== 1
    );
    return filteredItems;
  };

  const getYesterdayHistory = () => {
    const filteredItems = normalizedChatHistory.filter(
      (item) => isYesterday(item.timestamp) && item.pinned !== 1
    );
    return filteredItems;
  };

  const getLast7DaysHistory = () => {
    const filteredItems = normalizedChatHistory.filter(
      (item) => isWithinLast7Days(item.timestamp) && item.pinned !== 1
    );
    return filteredItems;
  };

  const getLast30DaysHistory = () => {
    const filteredItems = normalizedChatHistory.filter(
      (item) => isWithinLast30Days(item.timestamp) && item.pinned !== 1
    );
    return filteredItems;
  };

  const getOlderHistory = () => {
    const filteredItems = normalizedChatHistory.filter(
      (item) => isOlderThan30Days(item.timestamp) && item.pinned !== 1
    );
    return filteredItems;
  };

  const handleHistoryItemClickWithSidebarToggle = (item) => {
    // First call the original history item click handler
    handleHistoryItemClick(item);

    // Then close the sidebar if on mobile
    if (isSmallScreen) {
      toggleSidebar();
    }
  };

  // Helper functions for date categorization - moved to a utility file
  const dateUtils = {
    isToday: (date) => {
      if (!date || !(date instanceof Date)) return false;
      const today = new Date();
      return dateUtils.isSameUTCDay(date, today);
    },

    isYesterday: (date) => {
      if (!date || !(date instanceof Date)) return false;
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return dateUtils.isSameUTCDay(date, yesterday);
    },

    isSameUTCDay: (date1, date2) => {
      if (!date1 || !date2 || !(date1 instanceof Date) || !(date2 instanceof Date)) return false;
      const d1 = dateUtils.getUTCDateParts(date1);
      const d2 = dateUtils.getUTCDateParts(date2);
      return d1.year === d2.year && d1.month === d2.month && d1.day === d2.day;
    },

    getUTCDateParts: (date) => {
      if (!date || !(date instanceof Date)) return { year: 0, month: 0, day: 0 };
      return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth(),
        day: date.getUTCDate(),
      };
    },

    isWithinLast7Days: (date) => {
      if (!date || !(date instanceof Date)) return false;
      const now = new Date();
      for (let i = 2; i <= 7; i++) {
        const pastDate = new Date(now);
        pastDate.setDate(now.getDate() - i);
        if (dateUtils.isSameUTCDay(date, pastDate)) {
          return true;
        }
      }
      return false;
    },

    isWithinLast30Days: (date) => {
      if (!date || !(date instanceof Date)) return false;
      const now = new Date();
      for (let i = 8; i <= 30; i++) {
        const pastDate = new Date(now);
        pastDate.setDate(now.getDate() - i);
        if (dateUtils.isSameUTCDay(date, pastDate)) {
          return true;
        }
      }
      return false;
    },

    isOlderThan30Days: (date) => {
      if (!date || !(date instanceof Date)) return false;
      const now = new Date();
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      return date < thirtyDaysAgo;
    },
  };

  // Filter functions
  const historyFilters = {
    getTodayHistory: () => {
      return normalizedChatHistory.filter(
        (item) => dateUtils.isToday(item.timestamp) && item.pinned !== 1
      );
    },

    getYesterdayHistory: () => {
      return normalizedChatHistory.filter(
        (item) => dateUtils.isYesterday(item.timestamp) && item.pinned !== 1
      );
    },

    getLast7DaysHistory: () => {
      return normalizedChatHistory.filter(
        (item) =>
          dateUtils.isWithinLast7Days(item.timestamp) && item.pinned !== 1
      );
    },

    getLast30DaysHistory: () => {
      return normalizedChatHistory.filter(
        (item) =>
          dateUtils.isWithinLast30Days(item.timestamp) && item.pinned !== 1
      );
    },

    getOlderHistory: () => {
      return normalizedChatHistory.filter(
        (item) =>
          dateUtils.isOlderThan30Days(item.timestamp) && item.pinned !== 1
      );
    },
  };

  {
    /* Mobile Sidebar View */
  }
  if (isMobile) {
    return (
      <>
        {/* Success Toast Notification */}
        {showSuccessToast &&
          ReactDOM.createPortal(
            <div
              className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center feedsubmitPnc"
              style={{ left: "auto", right: "1rem" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {t('feedbackSubmittedSuccess')}
            </div>,
            document.body
          )}

        <aside
          className={`w-60 border-r border-gray-200  flex-shrink-0 transition-transform duration-300 ease-in-out sidebarPnc ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col">
            <div className="flex items-center p-4 border-gray-200 historyIconPnc">
              <button
                type="button"
                className="border-none p-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSidebar();
                }}
              >
                <img src={toggleBtn} alt="Toggle Button" />
              </button>
            </div>

            <div className="flex-grow">
              <h2 className="conversationHeadingPnc">{t('conversationHistory')}</h2>
              {isLoadingHistory && (
                <div className="text-center py-2">
                  <p className="text-gray-500">{t('loadingHistory')}</p>
                </div>
              )}

              {historyError && (
                <div className="text-center py-2">
                  <p className="text-red-500">{historyError}</p>
                </div>
              )}

              {!isLoadingHistory && !historyError && (
                <div className="space-y-4 historyTabPnc">
                  {/* Pinned Chats Section */}
                  {pinnedChats.length > 0 && (
                    <PinnedChats
                      pinnedChats={pinnedChats}
                      selectedHistoryId={selectedHistoryId}
                      handleHistoryItemClick={
                        handleHistoryItemClickWithSidebarToggle
                      }
                      handleLongPressStart={handleLongPressStart}
                      handleLongPressEnd={handleLongPressEnd}
                      toggleDropdown={toggleDropdown}
                      activeDropdownId={activeDropdownId}
                      dropdownRef={dropdownRef}
                      handleDeleteButtonClick={handleDeleteButtonClick}
                      handleUnpinButtonClick={handleUnpinButtonClick}
                      isMobile={isMobile}
                    />
                  )}

                  {/* Today's History */}
                  <HistorySection
                    title={t('Today')}
                    items={historyFilters.getTodayHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Yesterday's History */}
                  <HistorySection
                    title={t('Yesterday')}
                    items={historyFilters.getYesterdayHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Previous 7 Days History */}
                  <HistorySection
                    title={t('Past 7 Days')}
                    items={historyFilters.getLast7DaysHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Previous 30 Days History */}
                  <HistorySection
                    title={t('previous30Days')}
                    items={historyFilters.getLast30DaysHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Older History */}
                  <HistorySection
                    title={t('Older')}
                    items={historyFilters.getOlderHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* If no history at all */}
                  {normalizedChatHistory.length === 0 && (
                    <div className="text-center py-2">
                      <p className="text-gray-500 text-left">
                        {t('noConversationHistory')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fixed bottom section - restructured to prevent overlap */}
            <div class="fixed bottom-0 left-0 w-full bg-white p-3 z-50 flex flex-col sidebarBtnNew">
              {/* New Chat button */}
              <ActionButtons
                resetToWelcomePage={() => {
                  resetToWelcomePage();
                  if (window.innerWidth < 640) {
                    toggleSidebar();
                  }
                }}
                handleFeedbackButtonClick={handleFeedbackButtonClick}
                isMobile={isMobile}
              />
              {/* User profile section */}
              <ProfileSection handleProfileClick={handleProfileClick} />
            </div>
          </div>
        </aside>

        {/* Profile Menu - Mobile view */}
        {showProfileMenu &&
          ReactDOM.createPortal(
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
              onClick={handleCloseProfileMenu}
            >
              <div
                className="bg-white w-full profileSmallOuterPnc"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-rose-50 p-4 border-b border-gray-200 profileSmallPnc slideupModal">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-medium profileModalPnc">
                      {t('profile')}
                    </h2>
                    <button
                      onClick={handleCloseProfileMenu}
                      className="text-2xl text-gray-500 crossFeedBtnPnc"
                    >
                      <svg
                        className="svgFeedPnc"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="py-2">
                  <button
                    onClick={handleAboutChatbot}
                    className="w-full text-left px-4 py-3 flex items-center hover:bg-gray-100 border-none"
                  >
                    <span className="inline-block mr-3">
                      <img src={aboutSide} alt="About" className="w-6 h-6" />
                    </span>
                    <span>{t('aboutChatbot')}</span>
                  </button>

                  <button
                    onClick={handleLogoutClick}
                    className="w-full text-left px-4 py-3 flex items-center text-red-600 hover:bg-gray-100 border-none"
                  >
                    <span className="inline-block mr-3">
                      <img src={logoutPnc} alt="Logout" className="w-6 h-6" />
                    </span>
                    <span>{t('logout')}</span>
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* Mobile Feedback Modal - Slides from bottom with FeedbackModal content */}
        {showMobileFeedbackModal && (
          <MobileFeedbackModal
            rating={rating}
            setRating={setRating}
            feedback={feedback}
            setFeedback={setFeedback}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            isSubmitting={isSubmitting}
            submitError={submitError}
            handleSubmitFeedback={handleSubmitFeedback}
            handleCloseMobileFeedbackModal={handleCloseMobileFeedbackModal}
          />
        )}
        {/* Add CSS for the slide-up animation */}
        <style jsx>{`
          @keyframes slide-up {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }

          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          .animate-slide-up {
            animation: slide-up 0.3s ease-out forwards;
          }

          .animate-fade-in {
            animation: fade-in 0.2s ease-out forwards;
          }
        `}</style>
      </>
    );
  }

  // Desktop view 
  return (
    <>
      <aside
        className={`w-72 bg-rose-50 border-r border-gray-200 flex-shrink-0 transition-transform duration-300 ease-in-out sidebarPnc ${
          isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed lg:hidden"
        }`}
      >
        <div className="sidebarExpand">
          <div className="p-4 historyIconPnc">
            <div className="flex items-center space-x-2">
              <img src={fusoLogo} alt="FUSO" className="h-8" />
            </div>
            <div className="flex items-center ">
              <img
                src={historyClosePnc}
                alt="Toggle button"
                className="h-8 cursor-pointer ${isSidebarOpen ? 'toggle-open' : 'toggle-closed'}`"
                onClick={toggleSidebar}
              />
            </div>
          </div>
          <div className="sideButtonPnc">
            <ActionButtons
              resetToWelcomePage={resetToWelcomePage}
              handleFeedbackButtonClick={toggleFeedbackModal}
              isMobile={false}
            />
            {/* History Section in Sidebar */}
            <div className="space-y-4">
              <h2 className="conversationHeadingPnc">{t('conversationHistory')}</h2>
              {historyError && (
                <div className="text-center py-2">
                  <p className="text-red-500">{historyError}</p>
                </div>
              )}

              {!isLoadingHistory && !historyError && (
                <div className="space-y-4 historyTabPnc">
                  {/* Pinned Chats Section */}
                  {pinnedChats.length > 0 && (
                    <PinnedChats
                      pinnedChats={pinnedChats}
                      selectedHistoryId={selectedHistoryId}
                      handleHistoryItemClick={
                        handleHistoryItemClickWithSidebarToggle
                      }
                      toggleDropdown={toggleDropdown}
                      activeDropdownId={activeDropdownId}
                      dropdownRef={dropdownRef}
                      handleDeleteButtonClick={handleDeleteButtonClick}
                      handleUnpinButtonClick={handleUnpinButtonClick}
                      isMobile={isMobile}
                    />
                  )}

                  {/* Today's History */}
                  <HistorySection
                    title={t('Today')}
                    items={historyFilters.getTodayHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Yesterday's History */}
                  <HistorySection
                    title={t('Yesterday')}
                    items={historyFilters.getYesterdayHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Previous 7 Days History */}
                  <HistorySection
                    title={t('Past 7 Days')}
                    items={historyFilters.getLast7DaysHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Previous 30 Days History */}
                  <HistorySection
                    title={t('previous30Days')}
                    items={historyFilters.getLast30DaysHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* Older History */}
                  <HistorySection
                    title={t('Older')}
                    items={historyFilters.getOlderHistory()}
                    selectedHistoryId={selectedHistoryId}
                    handleHistoryItemClick={
                      handleHistoryItemClickWithSidebarToggle
                    }
                    toggleDropdown={toggleDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownRef={dropdownRef}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    handlePinChat={handlePinChat}
                  />

                  {/* If no history at all */}
                  {normalizedChatHistory.length === 0 && (
                    <div className="text-center py-2">
                      <p className="text-gray-500 text-left">
                        {t('noConversationHistory')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
           <div className="feedSectionPnc">
              <div
                className="feedbackPnc cursor-pointer"
                onClick={toggleFeedbackModal}
              >
                <img src={feedPnc} alt="Give Feedback icon" />
                {t('giveFeedback')}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Success Toast Notification that can be shown from desktop or mobile */}
      {showSuccessToast &&
        ReactDOM.createPortal(
          <div
            className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center feedsubmitPnc"
            style={{ left: "auto", right: "1rem" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            {t('feedbackSubmittedSuccess')}
          </div>,
          document.body
        )}
    </>
  );
};

export default Sidebar;